import { queryOptions, skipToken } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';

/**
 * EU-593 — catálogos do drawer "Filtrar candidatos" (proxies do BFF para os
 * endpoints de educação do Core). A UI sugere 4 opções por vez (RN do drawer);
 * o corte de 4 é client-side, sobre o resultado do backend.
 */

export interface IdName {
  id: number;
  name: string;
}

class CatalogFetchError extends Error {
  readonly name = 'CatalogFetchError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

async function fetchCatalog<T>(url: string, signal?: AbortSignal): Promise<T> {
  const res = await fetchClient<T>({ method: 'GET', url, signal });
  if (res.error) {
    throw new CatalogFetchError(`Failed to load catalog (${url})`, res.error.status);
  }
  return res.data;
}

export function courseTypesCatalogQueryOptions() {
  return queryOptions({
    queryKey: ['recruiter', 'programs', 'filters', 'course-types'] as const,
    queryFn: ({ signal }) =>
      fetchCatalog<{ courseTypes: (IdName & { key: string })[] }>(
        '/programs/filters/course-types',
        signal
      ),
    staleTime: 5 * 60_000,
  });
}

export function courseAreasCatalogQueryOptions() {
  return queryOptions({
    queryKey: ['recruiter', 'programs', 'filters', 'course-areas'] as const,
    queryFn: ({ signal }) =>
      fetchCatalog<{ courseAreas: IdName[] }>('/programs/filters/course-areas', signal),
    staleTime: 5 * 60_000,
  });
}

/** Busca server-side com gate de 2 chars (regra server-side-search). */
export function courseGroupsSearchQueryOptions(term: string) {
  const query = term.trim();
  const enabled = query.length >= 2;
  return queryOptions({
    queryKey: ['recruiter', 'programs', 'filters', 'course-groups', query] as const,
    queryFn: enabled
      ? ({ signal }) =>
          fetchCatalog<{ courseGroups: IdName[] }>(
            `/programs/filters/course-groups?query=${encodeURIComponent(query)}`,
            signal
          )
      : skipToken,
    staleTime: 5 * 60_000,
  });
}
